import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AdminLayout from "./AdminLayout";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { MapPin, Briefcase, Users, Euro, Calendar, Eye, Plus, Pencil, Copy, Save, Trash2, Layers, FilePlus } from "lucide-react";

const STATUS_OPTS = ["draft", "published", "paused", "closed", "archived"];
const STATUS_COLOR = {
  published: "bg-[#2E7D32]/10 text-[#2E7D32]", draft: "bg-slate-100 text-slate-600",
  paused: "bg-[#C79A3B]/15 text-[#8a6a1e]", closed: "bg-red-100 text-red-600", archived: "bg-slate-200 text-slate-500",
};
const TEXT_FIELDS = [
  ["title", "Job title"], ["department", "Department"], ["category", "Category"], ["location", "Location"],
  ["location_short", "Location (short)"], ["employment_type", "Employment type"], ["work_style", "Work style"],
  ["level", "Level"], ["reporting_to", "Reporting to"], ["territory", "Territory"], ["salary_display", "Salary display text"],
  ["hiring_manager", "Hiring manager"], ["opening_date", "Opening date"], ["closing_date", "Closing date"],
];
const LIST_FIELDS = [
  ["candidate_profile", "Candidate profile (one per line)"], ["highly_desirable", "Highly desirable (one per line)"],
  ["success_first_12_months", "Success in first 12 months (one per line)"], ["remuneration", "Remuneration (one per line)"],
];

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [editing, setEditing] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = ["super_admin", "recruitment_admin"].includes(user?.admin_role);

  const [templates, setTemplates] = useState([]);
  const [tplJob, setTplJob] = useState(null);
  const [tplName, setTplName] = useState("");
  const load = useCallback(async () => { const { data } = await api.get("/admin/jobs"); setJobs(data); }, []);
  const loadTemplates = useCallback(async () => { const { data } = await api.get("/admin/job-templates"); setTemplates(data); }, []);
  useEffect(() => { load(); loadTemplates(); }, [load, loadTemplates]);
  const saveAsTemplate = async () => {
    try { await api.post(`/admin/jobs/${tplJob.id}/save-as-template`, { name: tplName || tplJob.title }); toast.success("Saved as template"); setTplJob(null); setTplName(""); loadTemplates(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const applyTemplate = async (t) => { try { await api.post(`/admin/jobs/from-template/${t.id}`); toast.success(`New draft created from "${t.name}"`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const deleteTemplate = async (t) => { await api.delete(`/admin/job-templates/${t.id}`); toast.success("Template deleted"); loadTemplates(); };

  const openNew = () => setEditing({ _new: true, status: "draft", salary_display: "As per the market and Irish experience.",
    candidate_profile: [], highly_desirable: [], success_first_12_months: [], remuneration: [], about: "", opportunity: "" });
  const openEdit = (j) => setEditing({ ...j });

  const save = async () => {
    if (!editing.title?.trim()) return toast.error("Job title is required");
    const payload = { ...editing };
    LIST_FIELDS.forEach(([k]) => { if (typeof payload[k] === "string") payload[k] = payload[k].split("\n").map((s) => s.trim()).filter(Boolean); });
    try {
      if (editing._new) { await api.post("/admin/jobs", payload); toast.success("Job created"); }
      else { await api.put(`/admin/jobs/${editing.id}`, payload); toast.success("Job updated"); }
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const duplicate = async (j) => { try { await api.post(`/admin/jobs/${j.id}/duplicate`); toast.success("Job duplicated"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const changeStatus = async (j, status) => { await api.put(`/admin/jobs/${j.id}`, { status }); toast.success(`${j.title} → ${status}`); load(); };

  const val = (k) => (Array.isArray(editing?.[k]) ? editing[k].join("\n") : (editing?.[k] ?? ""));
  const setVal = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  return (
    <AdminLayout title="Job Management" actions={canManage &&
      <Button data-testid="new-job-button" onClick={openNew} className="bg-[#C79A3B] text-[#101820] font-semibold rounded-full"><Plus className="h-4 w-4 mr-1" /> New Job</Button>}>
      {!canManage && <div className="bg-[#101820]/5 border border-[#C79A3B]/25 rounded-lg p-3 text-xs text-[#101820]/70">Your role has read-only access to jobs. Only Super Admin or Recruitment Admin can create or edit vacancies.</div>}
      {canManage && templates.length > 0 && (
        <div className="bg-white rounded-lg border border-[#E4C675]/50 shadow-sm p-4">
          <div className="flex items-center gap-2 mb-3"><Layers className="h-4 w-4 text-[#C79A3B]" /><h3 className="font-heading font-semibold text-[#101820] text-sm">Job Templates</h3></div>
          <div className="flex flex-wrap gap-2">
            {templates.map((t) => (
              <div key={t.id} data-testid={`template-chip-${t.id}`} className="flex items-center gap-2 bg-[#FAF6EE] border border-[#C79A3B]/30 rounded-full pl-3 pr-1 py-1">
                <span className="text-sm text-[#101820]">{t.name}</span>
                <Button data-testid={`use-template-${t.id}`} size="sm" onClick={() => applyTemplate(t)} className="h-7 bg-[#101820] text-[#FAF6EE] rounded-full text-xs"><FilePlus className="h-3.5 w-3.5 mr-1" /> New</Button>
                <button data-testid={`delete-template-${t.id}`} onClick={() => deleteTemplate(t)} className="text-[#C76A32] hover:bg-[#C76A32]/10 rounded-full w-6 h-6 grid place-items-center"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-6">
        {jobs.map((j) => (
          <div key={j.id} data-testid={`job-card-${j.slug}`} className="bg-white rounded-lg border border-[#E4C675]/50 shadow-sm p-6">
            <div className="flex items-start justify-between">
              <div>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-semibold ${STATUS_COLOR[j.status] || "bg-slate-100"}`}>{j.status}</span>
                <h3 className="font-heading text-xl font-bold text-[#101820] mt-2">{j.title}</h3>
                <p className="text-sm text-muted-foreground">{j.department}</p>
              </div>
              {canManage && (
                <Select value={j.status} onValueChange={(v) => changeStatus(j, v)}>
                  <SelectTrigger data-testid={`job-status-${j.slug}`} className="w-32 h-8 text-xs bg-[#FAF6EE]"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_OPTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm text-[#101820]/75">
              <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#C79A3B]" /> {j.location_short || "—"}</div>
              <div className="flex items-center gap-2"><Briefcase className="h-4 w-4 text-[#C79A3B]" /> {j.employment_type}</div>
              <div className="flex items-center gap-2 col-span-2"><Euro className="h-4 w-4 text-[#C79A3B]" /> {j.salary_display}</div>
              <div className="flex items-center gap-2"><Users className="h-4 w-4 text-[#C79A3B]" /> {j.applications_count} applicants</div>
              <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-[#C79A3B]" /> {j.opening_date} → {j.closing_date}</div>
            </div>
            <div className="flex flex-wrap gap-2 mt-5">
              <Button variant="outline" onClick={() => navigate(`/jobs/${j.slug}`)} className="border-[#C79A3B]/40"><Eye className="h-4 w-4 mr-1" /> Public page</Button>
              <Button onClick={() => navigate("/admin/candidates")} className="bg-[#101820] text-[#FAF6EE]">Applicants</Button>
              {canManage && <Button data-testid={`edit-job-${j.slug}`} variant="outline" onClick={() => openEdit(j)} className="border-[#C79A3B]/40"><Pencil className="h-4 w-4 mr-1" /> Edit</Button>}
              {canManage && <Button data-testid={`duplicate-job-${j.slug}`} variant="outline" onClick={() => duplicate(j)} className="border-[#C79A3B]/40"><Copy className="h-4 w-4 mr-1" /> Duplicate</Button>}
              {canManage && <Button data-testid={`save-template-${j.slug}`} variant="outline" onClick={() => { setTplJob(j); setTplName(j.title); }} className="border-[#C79A3B]/40"><Save className="h-4 w-4 mr-1" /> Save as template</Button>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!tplJob} onOpenChange={(o) => !o && setTplJob(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Save job as template</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label className="text-xs">Template name</Label>
            <Input data-testid="template-name-input" value={tplName} onChange={(e) => setTplName(e.target.value)} className="bg-[#FAF6EE]" />
            <Button data-testid="save-template-confirm" onClick={saveAsTemplate} className="w-full bg-[#101820] text-[#FAF6EE]">Save template</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto scrollbar-thin">
          <DialogHeader><DialogTitle>{editing?._new ? "Create job" : "Edit job"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                {TEXT_FIELDS.map(([k, label]) => (
                  <div key={k}><Label className="text-xs">{label}</Label><Input data-testid={`job-field-${k}`} value={val(k)} onChange={(e) => setVal(k, e.target.value)} className="mt-1 bg-[#FAF6EE]" /></div>
                ))}
                <div><Label className="text-xs">Status</Label>
                  <Select value={editing.status} onValueChange={(v) => setVal("status", v)}>
                    <SelectTrigger className="mt-1 bg-[#FAF6EE]"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS_OPTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label className="text-xs">About</Label><Textarea data-testid="job-field-about" value={val("about")} onChange={(e) => setVal("about", e.target.value)} rows={4} className="mt-1 bg-[#FAF6EE]" /></div>
              <div><Label className="text-xs">The opportunity</Label><Textarea data-testid="job-field-opportunity" value={val("opportunity")} onChange={(e) => setVal("opportunity", e.target.value)} rows={4} className="mt-1 bg-[#FAF6EE]" /></div>
              {LIST_FIELDS.map(([k, label]) => (
                <div key={k}><Label className="text-xs">{label}</Label><Textarea data-testid={`job-field-${k}`} value={val(k)} onChange={(e) => setVal(k, e.target.value)} rows={3} className="mt-1 bg-[#FAF6EE]" /></div>
              ))}
              <Button data-testid="save-job-button" onClick={save} className="w-full bg-[#101820] text-[#FAF6EE]">{editing._new ? "Create job" : "Save changes"}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
